import React, { useState, useEffect, useRef } from "react";
import "../css/EditImage.css";
import "../css/CommonStyles.css";

import trimming_warning from '../assets/images/TRIMMING_INFOGRAPHIC.jpg';
import checkerboard from "../assets/images/checkerbox.png";

const Section = ({ imageToEdit, shape, bleedBorderImage, onSave, onCancel }) => {
  const [zoom, setZoom] = useState(1); // Default zoom level
  const [zoomSlider, setZoomSlider] = useState(0); // range
  const [rotation, setRotation] = useState(0); // Default rotation angle
  const [offsetX, setOffsetX] = useState(0); // Image offset for dragging
  const [offsetY, setOffsetY] = useState(0); // Image offset for dragging
  const [dragging, setDragging] = useState(false); // Track dragging state
  const [startX, setStartX] = useState(0); // Starting mouse X position
  const [startY, setStartY] = useState(0); // Starting mouse Y position
  
  const imageRef = useRef(null); // Reference for the image to edit
  const canvasRef = useRef(null); // Reference for the canvas

  const resetState = () => {
    setZoom(1);
    setZoomSlider(0);
    setRotation(0);
    setOffsetX(0);
    setOffsetY(0);
    setDragging(false);
    setStartX(0);
    setStartY(0);
  };

  useEffect(() => {
    resetState();
  }, [imageToEdit]);

  // Handle zoom change
  const handleZoomChange = (e) => {
    const sliderValue = parseFloat(e.target.value);
    setZoomSlider(sliderValue);

    if (sliderValue === 0) {
      setZoom(1);
    } else if (sliderValue > 0) {
      setZoom(sliderValue+1);
    } else {
      setZoom(1 / Math.abs(sliderValue-1));
    }
  };

  // Handle rotation change
  const handleRotationChange = (e) => setRotation(e.target.value);

  // Start dragging
  const startDrag = (e) => {
    e.preventDefault();
    setDragging(true);
    setStartX(e.clientX);
    setStartY(e.clientY);
  };

  // Drag image while holding down the mouse
  const dragImage = (e) => {
    if (!dragging) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    setStartX(e.clientX);
    setStartY(e.clientY);

    setOffsetX(prev => prev + dx);
    setOffsetY(prev => prev + dy);
  };

  // Stop dragging
  const stopDrag = () => {
    setDragging(false);
  };

  //PHONE AND TOUCH EVENTS
  // Start dragging (touch)
  const startTouchDrag = (e) => {
    const touch = e.touches[0];
    setDragging(true);
    setStartX(touch.clientX);
    setStartY(touch.clientY);
  };

  // Drag image (touch)
  const touchDragImage = (e) => {
    if (!dragging) return;
    const touch = e.touches[0];
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    setStartX(touch.clientX);
    setStartY(touch.clientY);

    setOffsetX((prev) => prev + dx);
    setOffsetY((prev) => prev + dy);
  };

  // Stop dragging (touch)
  const stopTouchDrag = () => {
    setDragging(false);
  };

  
  const handleSave = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const img = imageRef.current;
    if (!img) return;

    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    const maskImg = new Image();
    maskImg.crossOrigin = "anonymous";
    maskImg.src = shape;

    maskImg.onload = () => {
      const container = canvas.parentElement;
      const containerRect = container.getBoundingClientRect();

      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // Export resolution
      const EXPORT_SIZE = 3000;

      const maskAspectRatio = maskImg.width / maskImg.height;
      const containerAspectRatio = containerWidth / containerHeight;

      let fittedWidth, fittedHeight;

      if (maskAspectRatio > containerAspectRatio) {
        fittedWidth = containerWidth;
        fittedHeight = containerWidth / maskAspectRatio;
      } else {
        fittedHeight = containerHeight;
        fittedWidth = containerHeight * maskAspectRatio;
      }

      const scaleToExport = EXPORT_SIZE / Math.max(fittedWidth, fittedHeight);

      const canvasWidth = fittedWidth * scaleToExport;
      const canvasHeight = fittedHeight * scaleToExport;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // UI export scale
      const scaleFactor = canvasWidth / fittedWidth;

      const displayedWidth = 500;
      const baseScale = displayedWidth / naturalWidth;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      //
      // === 1. EXPORT WITH OFFWHITE (+ optional BLEED BORDER) ===
      //
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background
      ctx.fillStyle = "#f2f0e9";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw transformed image
      ctx.save();

      ctx.translate(
        canvas.width / 2 + offsetX * scaleFactor,
        canvas.height / 2 + offsetY * scaleFactor
      );

      ctx.rotate((rotation * Math.PI) / 180);

      ctx.scale(
        baseScale * zoom * scaleFactor,
        baseScale * zoom * scaleFactor
      );

      ctx.drawImage(
        img,
        -naturalWidth / 2,
        -naturalHeight / 2,
        naturalWidth,
        naturalHeight
      );

      ctx.restore();

      // Apply mask
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = "source-over";

      // Optional bleed border
      if (bleedBorderImage) {
        const bleedBorderImg = new Image();
        bleedBorderImg.crossOrigin = "anonymous";
        bleedBorderImg.src = bleedBorderImage;

        bleedBorderImg.onload = () => {
          ctx.drawImage(bleedBorderImg, 0, 0, canvas.width, canvas.height);
          finalizeExport();
        };

        bleedBorderImg.onerror = () => {
          console.error("Error loading bleed border:", bleedBorderImage);
          finalizeExport();
        };
      } else {
        finalizeExport();
      }

      function finalizeExport() {
        const editedWithOffwhite = canvas.toDataURL("image/png");

        //
        // === 2. EXPORT WITHOUT OFFWHITE ===
        //
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.save();

        ctx.translate(
          canvas.width / 2 + offsetX * scaleFactor,
          canvas.height / 2 + offsetY * scaleFactor
        );

        ctx.rotate((rotation * Math.PI) / 180);

        ctx.scale(
          baseScale * zoom * scaleFactor,
          baseScale * zoom * scaleFactor
        );

        ctx.drawImage(
          img,
          -naturalWidth / 2,
          -naturalHeight / 2,
          naturalWidth,
          naturalHeight
        );

        ctx.restore();

        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "source-over";

        const editedWithoutOffwhite = canvas.toDataURL("image/png");

        onSave({
          original: imageToEdit.original,
          editedWithOffwhite,
          editedWithoutOffwhite,
          hasBeenEdited: true,
        });
      }
    };

    maskImg.onerror = () => {
      console.error("Error loading mask image:", shape);
    };
  };

  useEffect(() => {
    const imgElement = imageRef.current;

    if (!imgElement) return;

    const handleTouchStart = (e) => {
      e.preventDefault();
      startTouchDrag(e);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      touchDragImage(e);
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      stopTouchDrag();
    };

    // Attach listeners with passive: false
    imgElement.addEventListener("touchstart", handleTouchStart, { passive: false });
    imgElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    imgElement.addEventListener("touchend", handleTouchEnd, { passive: false });
    imgElement.addEventListener("touchcancel", handleTouchEnd, { passive: false });

    // Cleanup
    return () => {
      imgElement.removeEventListener("touchstart", handleTouchStart);
      imgElement.removeEventListener("touchmove", handleTouchMove);
      imgElement.removeEventListener("touchend", handleTouchEnd);
      imgElement.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [dragging, startX, startY]);


  return (
    <div className="SectionDetails">
      <div className={`trimming-warning phone-only ${bleedBorderImage ? "hidden" : ""}`}>
        <img
          src={trimming_warning}
          alt="Trimming Warning"
          className="trimming-warning-image phone-only"
        />
      </div>

      <div className="subtitleDivE">
        <span>Click and drag the image to move it,</span>
        <span>and use the sliders to rotate or zoom the image.</span>
        <span>So it fits as much as possible inside the frame.</span>
      </div>

      <div className="editor-layout">
        <div className={`trimming-warning desktop-only ${bleedBorderImage ? "hidden" : ""}`}>
          <img
            src={trimming_warning}
            alt="Trimming Warning"
            className="trimming-warning-image desktop-only"
          />
        </div>

        <div className="edit-image-container">

            <div
              className="masked-layer"
              style={{
                maskImage: `url("${shape}")`,
                WebkitMaskImage: `url("${shape}")`,
              }}
            >
              {/* everything that should be clipped */}
              {bleedBorderImage && (
                <img src={bleedBorderImage} className="bleed-border-overlay" />
              )}

              <div
                className="checkerboard-layer"
                style={{
                  backgroundImage: `url(${checkerboard})`,
                }}
              />

              <img src={shape} className="mask-outline" />

              {/* Editable image */}
              <div
                className="editable-image-container"
                onMouseMove={dragImage}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
              >
                <img
                  ref={imageRef}
                  src={imageToEdit?.original}
                  crossOrigin="anonymous"
                  alt="Editable"
                  className="editable-image"
                  style={{
                    transform: `translate(${offsetX}px, ${offsetY}px) scale(${zoom}) rotate(${rotation}deg)`,
                    width: `${500}px`,
                    height: "auto",
                  }}
                  onMouseDown={startDrag}
                />
              </div>
            </div>
            {/* Hidden canvas for saving */}
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
          </div>
        </div>

          <div className="reset-button-wrapper">
            <button onClick={resetState} className="reset-button-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill="#1f1f1f"
              >
                <path d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z" />
              </svg>
            </button>
          </div>

          {/* Controls */}
          <div className="controls">
            <div className="control-group">
              <label htmlFor="zoom">Zoom:</label>
              <input
                id="zoom"
                type="range"
                min="-6"
                max="6"
                step="0.01"
                value={zoomSlider}
                onChange={handleZoomChange}
              />
            </div>

            <div className="control-group">
              <label htmlFor="rotation">Rotate:</label>
              <input
                id="rotation"
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotation}
                onChange={handleRotationChange}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="buttons">
            <button onClick={onCancel} className="cancel-button">Cancel</button>
            <button onClick={handleSave} className="save-button">Save</button>
          </div>
    </div>
  );
};

export default Section;